import { defineMessages, type MessageDescriptor } from "react-intl";

/**
 * Die Anzeigeabschnitte eines Match-Ergebnisses — die eine Liste, gegen die sowohl das
 * Ergebnis-Dashboard als auch der Speichern-Dialog und das Unternehmens-Ranking arbeiten.
 *
 * WARUM EINE LISTE UND KEINE ACHT JSX-BLOECKE: Die Sichtbarkeit muss an drei Stellen dieselbe
 * Antwort geben (Dashboard, Speichern-Dialog, Unternehmens-Ranking). Stuende die Reihenfolge nur im
 * JSX, muesste jede der drei Stellen sie erneut aufzaehlen, und die vierte Box waere irgendwo
 * vergessen. Hier steht sie einmal; das Dashboard rendert daraus, die beiden Dialoge
 * beschriften daraus.
 *
 * DIE IDs SIND EIN DATENVERTRAG. Sie landen als Zeichenketten in `MatchResult.hiddenSections`
 * in Genesis und stehen dort dauerhaft. Umbenennen heisst: gespeicherte Ergebnisse zeigen den
 * Abschnitt wieder an, weil ihre alte Kennung niemanden mehr trifft. Neue Abschnitte dagegen
 * sind gefahrlos — sie fehlen in jeder gespeicherten Liste und sind damit sichtbar, was die
 * gewollte Voreinstellung ist (deshalb speichert Genesis die AUSGEBLENDETEN, nicht die
 * sichtbaren).
 *
 * Genesis validiert die Kennungen bewusst nicht: welche Boxen es gibt, ist Spark-Vokabular.
 * Eine unbekannte Kennung in der Liste blendet schlicht nichts aus.
 */
export const RESULT_SECTION_IDS = [
  "dataQuality",
  "team",
  "analysis",
  "topPowers",
  "powerRadar",
  "profileStructure",
  "skillStructure",
  "agents",
  // Die drei Inhalte der Details-Box einzeln, nicht die Box als Ganzes. Eine Kennung fuer den
  // Container waere eine zweite Ebene: der Nutzer haette eine Matrix eingeschaltet und saehe sie
  // trotzdem nicht, weil die Huelle aus ist. Die Box zeigt sich, sobald eines der drei sichtbar
  // ist, und verschwindet mit dem letzten — ein leerer Kasten mit Ueberschrift entsteht nie.
  "requirements",
  "powerMatrix",
  "skillMatrix",
] as const;

export type ResultSectionId = (typeof RESULT_SECTION_IDS)[number];

/**
 * Abschnitte des Unternehmens-Rankings. Eigene Kennungen trotz gleicher Speicherspalte: die Flaeche
 * zeigt andere Boxen als das Personen-Dashboard, und ein geteilter Name wuerde bedeuten, dass
 * ein im Personen-Ergebnis ausgeblendeter Abschnitt im Unternehmens-Ergebnis mit verschwindet.
 */
export const COMPANY_RESULT_SECTION_IDS = ["companyTopFive", "companyBands", "companyInsights"] as const;

export type CompanyResultSectionId = (typeof COMPANY_RESULT_SECTION_IDS)[number];

export type ResultSectionLabel = MessageDescriptor & { values?: Record<string, string | number> };

// Use the headline message IDs directly: labels must not acquire a second vocabulary.
export const resultSectionLabels: Record<ResultSectionId, ResultSectionLabel> = defineMessages({
  dataQuality: { id: "match.result.dataHint", defaultMessage: "Data note" },
  team: { id: "match.result.teamHeader", defaultMessage: "Team · {count, plural, one {# Person} other {# Personen}}" },
  analysis: { id: "match.result.insights.header", defaultMessage: "Match-Analyse" },
  topPowers: { id: "match.result.powersListHeader", defaultMessage: "Top Powers" },
  powerRadar: { id: "match.result.powersInTeam", defaultMessage: "Powers im Team" },
  profileStructure: { id: "match.result.structureHeader", defaultMessage: "Powers Profilstruktur" },
  skillStructure: { id: "match.result.skillStructureHeader", defaultMessage: "Top Skills" },
  agents: { id: "match.result.agentsTitle", defaultMessage: "Agenten" },
  requirements: { id: "match.result.factsAndSkills", defaultMessage: "Anforderungs-Matrix" },
  powerMatrix: { id: "match.result.teamPowers", defaultMessage: "Power-Scorecard-Matrix" },
  skillMatrix: { id: "match.result.skillMatrix", defaultMessage: "Fachkompetenz-Matrix" },
});

export function powerRadarSectionLabel(hasScorecard: boolean): ResultSectionLabel {
  return hasScorecard
    ? { id: "match.result.powersScorecardInTeam", defaultMessage: "Team vs Powers Scorecard" }
    : resultSectionLabels.powerRadar;
}

export function getResultSectionLabels(teamSize: number, hasScorecard: boolean): Record<ResultSectionId, ResultSectionLabel> {
  return {
    ...resultSectionLabels,
    team: { ...resultSectionLabels.team, values: { count: teamSize } },
    powerRadar: powerRadarSectionLabel(hasScorecard),
  };
}

export const companyResultSectionLabels: Record<CompanyResultSectionId, ResultSectionLabel> = defineMessages({
  companyTopFive: { id: "match.result.suitability.header", defaultMessage: "Top 5 – beste Eignung" },
  companyBands: { id: "match.result.rankingHeader", defaultMessage: "Ranking" },
  companyInsights: { id: "match.result.insights.header", defaultMessage: "Match-Analyse" },
});

/**
 * Ist der Abschnitt sichtbar? Alles, was nicht ausdruecklich ausgeblendet ist, ist sichtbar —
 * ein `null` aus Genesis (kein Wert gesetzt) und eine leere Liste bedeuten dasselbe.
 *
 * Nimmt `string`, nicht die engen Kennungs-Typen: die Liste kommt aus der Datenbank und kann
 * Kennungen enthalten, die diese Version nicht kennt (aelteres oder neueres Spark). Genau das
 * ist der Grund, warum Genesis sie nicht validiert — eine unbekannte Kennung trifft hier
 * niemanden und blendet nichts aus.
 */
export function isSectionVisible(id: string, hidden: readonly string[] | null | undefined): boolean {
  if ((id === "topPowers" || id === "powerRadar") && hidden?.includes("powers")) return false;
  return !hidden?.includes(id);
}

/** Schaltet eine Kennung in der Liste der ausgeblendeten Abschnitte um. */
export function toggleHiddenSection(
  hidden: readonly string[] | null | undefined,
  id: string,
): string[] {
  // Earlier results hid both boxes under one ID. Expand it when either toggle is edited.
  const current = (id === "topPowers" || id === "powerRadar") && hidden?.includes("powers")
    ? [...new Set([...hidden.filter((section) => section !== "powers"), "topPowers", "powerRadar"])]
    : hidden ?? [];
  return current.includes(id) ? current.filter((s) => s !== id) : [...current, id];
}
